import { type RefObject, useEffect, useRef, useState } from 'react';
import type { BattleEngine } from './useBattleCanvas';
import { playSfx } from './sfx';

const BEEP_FROM = 3; // beep once per second over the final BEEP_FROM seconds of the decision window

export const DECISION_S = 10; // seconds to choose AFTER the replay (server ROUND_TIMEOUT_MS is longer)

/**
 * Client-local decision timer: locked while THIS round's replay is still playing (read from
 * the engine), then a fixed DECISION_S window; auto-commits `onExpire` when it hits 0. The
 * single source of the "is the engine on this round + how far into the replay" check that the
 * reveal poll also needs.
 */
export function useRoundTimer(
  engineRef: RefObject<BattleEngine | null>,
  round: number,
  over: boolean,
  onExpire: () => void,
): { remain: number; replayLocked: boolean } {
  const [remain, setRemain] = useState(DECISION_S);
  // The unlock is stored WITH the round it belongs to: a new round's first render must read
  // as locked immediately (consumers act on replayLocked in the same effect flush the round
  // arrives in — an effect-based reset lands one flush late and leaks the previous round's
  // unlocked state into low-HP sound and target re-pick).
  const [lock, setLock] = useState({ round, locked: true });
  const replayLocked = lock.round !== round || lock.locked;
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;
  const lastBeepRef = useRef(0); // last whole-second we beeped, so each second beeps once

  useEffect(() => {
    if (over) return;
    lastBeepRef.current = 0;
    setLock({ round, locked: true });
    setRemain(DECISION_S);
    const id = window.setInterval(() => {
      const engine = engineRef.current;
      if (!engine || engine.getActiveRound() !== round) return; // assets/scene not ready yet
      const replayDur = engine.getReplayDur();
      const elapsed = performance.now() / 1000 - engine.getActiveAt();
      const locked = elapsed < replayDur;
      setLock({ round, locked });
      const dec = locked ? DECISION_S : replayDur + DECISION_S - elapsed;
      const secs = Math.max(0, Math.ceil(dec));
      setRemain(secs);
      // urgency beep on each of the final BEEP_FROM seconds — once per whole second (the 150ms
      // interval visits each second ~7×), only in the live decision phase (not while locked)
      if (!locked && secs > 0 && secs <= BEEP_FROM && secs !== lastBeepRef.current) {
        lastBeepRef.current = secs;
        playSfx('timer_beep', 0.5);
      }
      if (!locked && dec <= 0) onExpireRef.current();
    }, 150);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round, over]);

  return { remain, replayLocked };
}
